import { Button, Modal, Slider } from "@marketplace/ui";
import { notifications } from "@marketplace/ui/notifications";
import { useAtomValue, useSetAtom } from "jotai";
import { useResetAtom } from "jotai/utils";
import { useCallback, useMemo, useState } from "react";
import Cropper, { type Area } from "react-easy-crop";
import type { UseFormReturn } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { MdZoomIn, MdZoomOut } from "react-icons/md";

import { usePostGeneratePresignedUrlMutation } from "@/hooks";
import { editImageModalAtom } from "@/modules/product/atoms/add-product-page-atom";
import { getCroppedImg } from "@/utils";

import type { AddProductFormSchema } from "../../hooks/schemas/use-add-product-form-schema";
import type { EditProductFormSchema } from "../../hooks/schemas/use-edit-product-form-schema";

type EditImageModalProps = {
  formMethods: UseFormReturn<AddProductFormSchema | EditProductFormSchema>;
};

export const EditImageModal: React.FC<EditImageModalProps> = ({
  formMethods
}) => {
  const { visible, originalPictureFile } = useAtomValue(editImageModalAtom);
  const setEditImageModal = useSetAtom(editImageModalAtom);
  const resetEditImageModal = useResetAtom(editImageModalAtom);

  const { t } = useTranslation("product", {
    keyPrefix: "modal.editImageModal"
  });
  const { t: tCommon } = useTranslation("common");

  const { mutateAsync: mutateGeneratePresignedUrlAsync, isPending } =
    usePostGeneratePresignedUrlMutation();

  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);

  const imageUrl = useMemo(
    () =>
      originalPictureFile
        ? URL.createObjectURL(originalPictureFile)
        : undefined,
    [originalPictureFile]
  );

  const onCropComplete = useCallback(
    (_croppedArea: Area, croppedAreaPixels: Area) => {
      setCroppedAreaPixels(croppedAreaPixels);
    },
    []
  );

  const onSave = useCallback(async () => {
    try {
      if (!originalPictureFile || !croppedAreaPixels) return;

      const croppedImg = await getCroppedImg(
        URL.createObjectURL(originalPictureFile),
        croppedAreaPixels
      );

      if (!croppedImg) return;

      let newMediaPath = formMethods.getValues("picture.mediaPath");

      if (!newMediaPath || newMediaPath === "" || newMediaPath === "-") {
        const response = await mutateGeneratePresignedUrlAsync({
          mime_type: croppedImg.type,
          size: croppedImg.size,
          type: "image"
        });
        newMediaPath = response?.data?.media_path || "";
      }
      formMethods.setValue(
        "picture",
        {
          type: "image",
          mediaPath: newMediaPath
        },
        { shouldValidate: true, shouldDirty: true }
      );

      setEditImageModal({
        visible: false,
        originalPictureFile: null,
        editedPictureFile: croppedImg
      });
      setCrop({ x: 0, y: 0 });
      setZoom(1);
      setCroppedAreaPixels(null);
    } catch {
      notifications.show({
        variant: "error",
        message: t("notifications.failedToCropImage")
      });
    }
  }, [
    originalPictureFile,
    croppedAreaPixels,
    setEditImageModal,
    formMethods,
    mutateGeneratePresignedUrlAsync,
    t
  ]);

  return (
    <Modal
      title={t("title")}
      opened={visible}
      onClose={resetEditImageModal}
      size={375}
      classNames={{
        header: "!p-6 !pb-0",
        body: "!p-6"
      }}
    >
      <div className="w-full aspect-square bg-gray-100 mb-6 relative">
        <Cropper
          image={imageUrl}
          crop={crop}
          zoom={zoom}
          aspect={1}
          onCropChange={setCrop}
          onCropComplete={onCropComplete}
          onZoomChange={setZoom}
        />
      </div>

      <div className="flex items-center gap-2">
        <MdZoomOut size={24} />
        <Slider
          label={null}
          defaultValue={1}
          min={1}
          max={3}
          step={0.1}
          value={zoom}
          onChange={setZoom}
          className="flex-1"
        />
        <MdZoomIn size={24} />
        <Button
          variant="primary"
          onClick={onSave}
          loading={isPending}
          className="w-[78px]"
        >
          {tCommon("save")}
        </Button>
      </div>
    </Modal>
  );
};
